import {
  CARD_BRAND_BADGE_HEIGHT_PX,
  CARD_BRAND_BADGE_WIDTH_PX,
  INVALID_CARD_BADGE_WIDTH_PX,
  type CardBrandLaneState,
} from './card-brand-geometry';

export type CardBrandBadgeState = CardBrandLaneState;

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

const createSvg = (width: number, height: number, viewBox: string): SVGSVGElement => {
  const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  svg.setAttribute('width', String(width));
  svg.setAttribute('height', String(height));
  svg.setAttribute('viewBox', viewBox);
  svg.style.display = 'block';
  svg.style.flex = 'none';
  return svg;
};

const appendRect = (svg: SVGElement, attributes: Record<string, string>): SVGRectElement => {
  const rect = document.createElementNS(SVG_NAMESPACE, 'rect');
  for (const [name, value] of Object.entries(attributes)) rect.setAttribute(name, value);
  svg.appendChild(rect);
  return rect;
};

const appendPath = (svg: SVGElement, attributes: Record<string, string>): SVGPathElement => {
  const path = document.createElementNS(SVG_NAMESPACE, 'path');
  for (const [name, value] of Object.entries(attributes)) path.setAttribute(name, value);
  svg.appendChild(path);
  return path;
};

const createVisaBadge = (): SVGSVGElement => {
  const svg = createSvg(CARD_BRAND_BADGE_WIDTH_PX, CARD_BRAND_BADGE_HEIGHT_PX, '0 0 48 28');
  appendRect(svg, {
    x: '0.5',
    y: '0.5',
    width: '47',
    height: '27',
    rx: '3.5',
    fill: '#ffffff',
    stroke: '#d7dde0',
  });
  appendPath(svg, {
    d: 'M15.875 19.02h-2.726l-2.044-7.835c-.097-.36-.303-.679-.606-.829-.757-.378-1.59-.678-2.499-.829v-.302h4.392c.606 0 1.06.453 1.136.979l1.061 5.652 2.725-6.631h2.65l-4.089 9.795Zm5.604 0h-2.575l2.121-9.795h2.574l-2.12 9.795Zm5.451-7.08c.076-.528.53-.83 1.061-.83.833-.075 1.741.076 2.499.452l.454-2.109C30.187 9.152 29.353 9 28.597 9c-2.499 0-4.317 1.357-4.317 3.24 0 1.432 1.288 2.184 2.197 2.638.983.451 1.362.753 1.286 1.205 0 .678-.757.979-1.514.979-.909 0-1.818-.225-2.65-.603l-.454 2.11c.909.376 1.892.528 2.801.528 2.802.074 4.544-1.282 4.544-3.316 0-2.562-3.56-2.712-3.56-3.841ZM39.5 19.02l-2.044-9.795H35.26c-.455 0-.909.302-1.061.753L30.414 19.02h2.65l.529-1.431h3.257l.303 1.431H39.5Zm-3.861-7.157.756 3.692h-2.12l1.364-3.692Z',
    fill: '#172B85',
  });
  return svg;
};

const createMastercardBadge = (): SVGSVGElement => {
  const svg = createSvg(CARD_BRAND_BADGE_WIDTH_PX, CARD_BRAND_BADGE_HEIGHT_PX, '0 0 48 28');
  appendRect(svg, {
    x: '0.5',
    y: '0.5',
    width: '47',
    height: '27',
    rx: '3.5',
    fill: '#ffffff',
    stroke: '#d7dde0',
  });
  appendPath(svg, {
    d: 'M23.1 7.7a8.1 8.1 0 1 0 0 12.6 8.1 8.1 0 0 1 0-12.6Z',
    fill: '#EB001B',
  });
  appendPath(svg, {
    d: 'M24 8.5a8.1 8.1 0 0 0 0 11 8.1 8.1 0 0 0 0-11Z',
    fill: '#FF5F00',
  });
  appendPath(svg, {
    d: 'M24.9 7.7a8.1 8.1 0 1 1 0 12.6 8.1 8.1 0 0 0 0-12.6Z',
    fill: '#F79E1B',
  });
  return svg;
};

const createInvalidBadge = (): SVGSVGElement => {
  const svg = createSvg(INVALID_CARD_BADGE_WIDTH_PX, CARD_BRAND_BADGE_HEIGHT_PX, '0 0 36 28');
  svg.style.color = 'currentColor';
  appendRect(svg, {
    x: '1',
    y: '2',
    width: '27',
    height: '20',
    rx: '3',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
  });
  appendPath(svg, { d: 'M2 8h25', stroke: 'currentColor', 'stroke-width': '2' });
  appendPath(svg, {
    d: 'M27.7 11.1a2 2 0 0 1 3.6 0l4.4 9a2 2 0 0 1-1.8 2.9h-8.8a2 2 0 0 1-1.8-2.9l4.4-9Z',
    fill: 'currentColor',
  });
  appendPath(svg, {
    d: 'M29.5 14.4v3.4m0 2.3v.1',
    stroke: '#ffffff',
    'stroke-linecap': 'round',
    'stroke-width': '1.8',
  });
  return svg;
};

export type CardBrandBadgeLabels = Record<CardBrandBadgeState, string>;

const ariaLabelFor = (state: CardBrandBadgeState, labels: CardBrandBadgeLabels): string => {
  return labels[state];
};

/** SDK-owned, CSP-independent card-brand presentation. */
export class CardBrandBadges {
  readonly root: HTMLDivElement;
  private readonly visa = createVisaBadge();
  private readonly mastercard = createMastercardBadge();
  private readonly invalid = createInvalidBadge();

  constructor(private readonly labels: CardBrandBadgeLabels) {
    this.root = document.createElement('div');
    this.root.dataset.paypercutCardBrandBadges = '';
    this.root.setAttribute('role', 'img');
    this.root.append(this.visa, this.mastercard, this.invalid);
    this.setState('accepted');
  }

  setState(state: CardBrandBadgeState): void {
    this.root.dataset.cardBrandState = state;
    this.root.setAttribute('aria-label', ariaLabelFor(state, this.labels));
    this.visa.style.display = state === 'accepted' || state === 'visa' ? 'block' : 'none';
    this.mastercard.style.display =
      state === 'accepted' || state === 'mastercard' ? 'block' : 'none';
    this.invalid.style.display = state === 'invalid' ? 'block' : 'none';
  }

  setVisible(visible: boolean): void {
    this.root.hidden = !visible;
    this.root.setAttribute('aria-hidden', visible ? 'false' : 'true');
  }
}
